import { useEffect, useState } from "react";
import { get, post } from "@/lib/api";
import { StatePill } from "@/components/StatePill";
import { CaropsBox } from "@/components/CaropsBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DoorClosed, FileCheck2, Users, ShieldAlert, ChevronDown } from "lucide-react";

const ACTIONS = [["confirm", "Confirm"], ["decline", "Decline"], ["override", "Override & confirm"], ["redirect", "Redirect"], ["hold", "Place hold"], ["alternative", "Offer alternative"]];
const bkState = (s) => ({ awaiting_confirmation: "awaiting_confirmation", confirmed: "confirmed", declined: "declined", on_hold: "on_hold", redirected: "redirected", alternative_offered: "alternative_offered", cancelled: "cancelled" }[s] || "needs_information");

export function RoomsActivities({ onChanged }) {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [carops, setCarops] = useState(null);
  const [showRooms, setShowRooms] = useState(false);
  const load = () => get("/rooms/bookings").then((d) => { setBookings(d.bookings.filter((b) => !/^TEST/i.test(b.ref || "") && !/^TEST/i.test(b.requester || "") && !/^TEST/i.test(b.id || ""))); onChanged?.(); });
  useEffect(() => { load(); get("/rooms/config").then((c) => { setRooms(c.rooms); setCarops(c.carops); }); }, []);

  return (
    <div className="space-y-4" data-testid="rooms-activities">
      <CaropsBox carops={carops ? { d: carops } : null} stage="d" />

      <div className="rounded-2xl border border-stone-200 bg-white" data-testid="room-wallets">
        <button onClick={() => setShowRooms((s) => !s)} className="flex w-full items-center justify-between px-5 py-3.5 text-left">
          <span className="flex items-center gap-2 font-display text-base font-semibold text-mill-ink"><DoorClosed className="h-4 w-4 text-mill-moss" /> Room wallets ({rooms.length})</span>
          <ChevronDown className={`h-4 w-4 text-mill-muted transition-transform ${showRooms ? "rotate-180" : ""}`} />
        </button>
        {showRooms && (
          <div className="border-t border-stone-100 p-4">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {rooms.map((r) => (
                <div key={r.id} className="rounded-xl bg-mill-alt/40 p-3 text-xs" data-testid={`room-${r.id}`}>
                  <p className="font-medium text-mill-ink">{r.name} <span className="text-mill-muted">· cap {r.capacity} · {r.hours.open}–{r.hours.close}</span></p>
                  <p className="text-mill-muted">{r.activities.join(", ")}</p>
                  <p className="mt-0.5 text-mill-muted">{r.accessibility}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-mill-muted">Each room's availability calendar stays private. Requesters see only availability and bounded alternatives.</p>
          </div>
        )}
      </div>

      <h3 className="font-display text-lg font-semibold text-mill-ink">Booking requests</h3>
      {bookings.length === 0 && <p className="text-sm text-mill-muted">No booking requests yet.</p>}
      {bookings.map((b) => <BookingRow key={b.id} b={b} onChange={load} />)}
    </div>
  );
}

function BookingRow({ b, onChange }) {
  const [msg, setMsg] = useState("");
  const decide = async (action) => {
    await post(`/rooms/bookings/${b.id}/decision`, { action, message: msg || "" });
    setMsg(""); await onChange(); toast.success(`Booking ${b.ref}: ${action}.`);
  };
  const open = b.status === "awaiting_confirmation" || b.status === "on_hold";
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`staff-booking-${b.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-mill-ink">{b.ref} · {b.activity_name} <span className="text-xs font-normal text-mill-muted">({b.activity_type})</span></p>
          <p className="text-xs capitalize text-mill-muted">{b.requester} ({b.requester_role}) · {b.room} · {b.day} · {b.start}–{b.end} · {b.attendance} people · {b.recurring}{b.recur_range ? ` (${b.recur_range})` : ""}</p>
        </div>
        <StatePill state={bkState(b.status)} />
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
        {(b.equipment || []).map((e) => <span key={e} className="rounded-full bg-mill-alt px-2 py-0.5 text-mill-ink">{e}</span>)}
        {b.safeguarding_involved && <span className="flex items-center gap-1 rounded-full bg-[#FEF2F2] px-2 py-0.5 text-[#991B1B]"><ShieldAlert className="h-3 w-3" /> Children / vulnerable adults</span>}
      </div>
      {b.reasons?.length > 0 && <p className="mt-1 text-xs text-[#B45309]">Requires confirmation: {b.reasons.join("; ")}.</p>}
      {b.setup && <p className="mt-1 text-xs text-mill-muted">Setup: {b.setup}</p>}
      {b.availability?.result_type === "unavailable" && <p className="mt-1 text-xs text-mill-muted">Requester's first choice was unavailable; they were offered {b.availability.alternatives?.length || 0} alternatives.</p>}

      {open ? (
        <div className="mt-3 space-y-2">
          <Input placeholder="Optional message to requester" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid={`bk-msg-${b.id}`} />
          <div className="flex flex-wrap gap-2">
            {ACTIONS.map(([a, label]) => (
              <Button key={a} size="sm" variant={a === "confirm" ? "default" : "outline"} className={a === "confirm" ? "bg-mill-moss hover:bg-mill-moss-dark" : ""} onClick={() => decide(a)} data-testid={`bk-${a}-${b.id}`}>{label}</Button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-xl bg-mill-alt/50 p-4 text-sm" data-testid={`bk-decision-${b.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-mill-muted">Decision record</p>
          {b.decision?.message && <p className="mt-1 text-xs text-mill-ink"><b>Message:</b> {b.decision.message}</p>}
          {b.decision?.evidence && <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-[#FFFDF5] px-3 py-2 text-xs text-[#B45309]" data-testid={`bk-evidence-${b.id}`}><FileCheck2 className="h-3.5 w-3.5" /> {b.decision.evidence.text} <span className="text-[10px] text-mill-muted">· {b.decision.evidence.verification}</span></p>}
        </div>
      )}
    </div>
  );
}
